import { Injectable } from '@angular/core';
import {
	PluginType,
	ArgumentationGameData,
	ArgumentItem,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Argumentation specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class ArgumentationGenerationService
	extends BaseGenerationService<ArgumentationGameData>
{
	readonly pluginType = PluginType.Argumentation;

	/**
	 * Create system and user prompts for argumentation game generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		let systemPrompt: string;
		let userPrompt: string;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		if (parameters['questionTopic']?.trim()) {
			// Topic-based generation
			systemPrompt = `Olet argumenttipelin suunnittelija. Tehtäväsi on luoda argumenttipeli, jossa käyttäjä rakentaa vahvan argumentin annetulle väitteelle valitsemalla vahvimmat perustelut. Luo argumentit, jotka ovat selkeitä ja loogisia, mutta tarpeeksi haastavia.

${targetLanguagePrompt}`;

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} argumenttia aiheesta: "${parameters['questionTopic']}"

Noudata seuraavia ohjeita:
- Luo selkeä väite, jota argumentit tukevat. Pidä väite lyhyenä.
- Luo ${parameters['numberOfQuestions'] || 5} perustelua/argumenttia väitteelle. Väitteiden tulee olla lyhyitä ja selkeitä.
- Pisteytä argumentit 1-5 pisteen väliltä tärkeyden mukaan. Tee väitteitä, joista pystyy päätellä niiden tärkeys. On tärkeää, että viiden pisteen väite on huomattaasti parempi kuin yhden pisteen väite.
- Luo palautetekstit eri pistemäärille

Vastaa JSON-muodossa:
{
    "title": "Argumenttipelin otsikko",
    "description": "Argumenttipelin kuvaus", 
    "claimText": "Väite, jota perustellaan",
    "items": [
        {
            "id": 1,
            "text": "Ensimmäinen perustelu/väite",
            "points": 5
        },
        {
            "id": 2,
            "text": "Toinen perustelu/väite", 
            "points": 4
        }
    ],
    "feedbackTexts": {
        "10": "Erinomainen argumentti! Valitsit parhaat perustelut.",
        "8": "Hyvä argumentti! Olet ymmärtänyt aiheen hyvin.",
        "5": "Kohtuullinen argumentti. Osa valinnoistasi toimii, mutta voisit parantaa.",
        "0": "Yritä uudestaan. Mieti mitkä perustelut tukisivat väitettä parhaiten."
    }
}`;
		} else {
			// Source material based generation
			systemPrompt = `Olet argumenttipelin suunnittelija. Tehtäväsi on luoda argumenttipeli annetusta lähdemateriaalista, jossa käyttäjä rakentaa vahvan argumentin valitsemalla vahvimmat perustelut. Selvitä, mistä lähdemateriaalissa on kyse ja luo siitä mielenkiintoisia argumentteja ja väitteitä.

${targetLanguagePrompt}`;

			const includeImages = sourceMaterial?.imagesAsString
				? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna argumenttien laatimisessa:\n${sourceMaterial.imagesAsString}`
				: '';

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} argumenttia valitusta lähdemateriaalista.${includeImages}

Noudata seuraavia ohjeita:
- Muodosta lähdemateriaalin pohjalta selkeä väite
- Luo ${parameters['numberOfQuestions'] || 5} perustelua/argumenttia väitteelle lähdemateriaalista
- Perusta argumentit lähdemateriaalin faktaisiin tietoihin
- Pisteytä argumentit 1-5 pisteen väliltä tärkeyden mukaan
- Varmista, että argumentit ovat uskottavia ja tukevat väitettä

Vastaa JSON-muodossa:
{
    "title": "Argumenttipelin otsikko",
    "description": "Argumenttipelin kuvaus",
    "claimText": "Väite, jota perustellaan (perustuva lähdemateriaaliin)",
    "items": [
        {
            "id": 1,
            "text": "Ensimmäinen perustelu/väite",
            "points": 5
        },
        {
            "id": 2, 
            "text": "Toinen perustelu/väite",
            "points": 4
        }
    ],
    "feedbackTexts": {
        "10": "Erinomainen argumentti! Valitsit parhaat perustelut.",
        "8": "Hyvä argumentti! Olet ymmärtänyt aiheen hyvin.",
        "5": "Kohtuullinen argumentti. Osa valinnoistasi toimii, mutta voisit parantaa.",
        "0": "Yritä uudestaan. Mieti mitkä perustelut tukisivat väitettä parhaiten."
    }
}`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into ArgumentationGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(response: any, parameters: any): GenerationResult {
		try {
			const generatedData = response.json;

			if (!generatedData) {
				return {
					success: false,
					error: 'Invalid response format: no data found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate structure
			if (
				!generatedData.items ||
				!Array.isArray(generatedData.items) ||
				generatedData.items.length === 0
			) {
				return {
					success: false,
					error: 'Invalid response format: no arguments found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate argument items
			for (const item of generatedData.items) {
				if (!item.text || typeof item.points !== 'number') {
					return {
						success: false,
						error: 'Invalid argument structure in response',
						rawResponse: response.rawResponse,
					};
				}
			}

			// Create game data
			const gameData = new ArgumentationGameData();
			gameData.title = generatedData.title || 'Argumenttipeli';
			gameData.description =
				generatedData.description ||
				'Rakenna vahvin argumentti annetulle väitteelle.';
			gameData.claimText =
				generatedData.claimText ||
				'Rakenna argumentti tälle väitteelle.';

			// Convert items to ArgumentItem format
			gameData.items = generatedData.items.map(
				(item: any, index: number) =>
					({
						id: item.id || index + 1,
						text: item.text,
						points: item.points || 1,
					}) as ArgumentItem,
			);

			// Set feedback texts if provided
			if (generatedData.feedbackTexts) {
				gameData.feedbackTexts = generatedData.feedbackTexts;
			}

			// Calculate max points based on items
			gameData.maxPoints = this.calculateMaxPoints(gameData);

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Calculate max possible points based on argument categories and items
	 */
	private calculateMaxPoints(gameData: ArgumentationGameData): number {
		if (!gameData.items || gameData.items.length === 0) {
			return 0;
		}

		// Sort items by points value (highest first)
		const sortedItems = [...gameData.items].sort(
			(a, b) => (b.points || 1) - (a.points || 1),
		);

		let maxPoints = 0;
		let itemIndex = 0;

		// Calculate max points based on optimal assignment to categories
		for (const category of gameData.argumentCategories) {
			for (
				let i = 0;
				i < category.maxItems && itemIndex < sortedItems.length;
				i++
			) {
				maxPoints +=
					category.pointsPerItem *
					(sortedItems[itemIndex].points || 1);
				itemIndex++;
			}
		}

		return maxPoints;
	}

	/**
	 * Validate argumentation specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;
		const hasTopic =
			request.parameters['questionTopic'] &&
			request.parameters['questionTopic'].trim();

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: 'Syötä aihe argumenteille tai valitse lähdemateriaali',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for argumentation
	 */
	getDefaultParameters(): any {
		return {
			numberOfQuestions: 5,
			clearPrevious: false,
		};
	}

	/**
	 * Merge new arguments with existing game data
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing arguments
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: ArgumentationGameData,
		newGameData: ArgumentationGameData,
		clearPrevious: boolean,
	): ArgumentationGameData {
		const newArguments = newGameData.items || [];

		if (clearPrevious) {
			existingGameData.items = newArguments;
		} else {
			// If first argument is empty placeholder, remove it
			if (
				existingGameData.items.length === 1 &&
				(!existingGameData.items[0].text ||
					existingGameData.items[0].text === 'Argumentti 1')
			) {
				existingGameData.items = [];
			}

			// Add new arguments to existing ones
			existingGameData.items.push(...newArguments);
		}

		// Update max points
		existingGameData.maxPoints = this.calculateMaxPoints(existingGameData);

		return existingGameData;
	}
}
